import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/lib/api'
import type { CreateRulePayload } from '@/lib/referral-types'
import { TRIGGER_EVENTS, REFERRER_REWARD_TYPES, REFERRED_REWARD_TYPES } from '@/lib/referral-utils'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'

interface RuleDraft extends CreateRulePayload {
  _key: number
}

let nextKey = 1

export function CampaignCreatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState('draft')
  const [referrerType, setReferrerType] = useState('any')
  const [referredProduct, setReferredProduct] = useState('academy')
  const [attributionWindowDays, setAttributionWindowDays] = useState('')
  const [maxPerUser, setMaxPerUser] = useState('')
  const [maxTotal, setMaxTotal] = useState('')
  const [pendingDays, setPendingDays] = useState('14')
  const [minPayoutCents, setMinPayoutCents] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [rules, setRules] = useState<RuleDraft[]>([])
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function addRule() {
    setRules((prev) => [
      ...prev,
      {
        _key: nextKey++,
        trigger_event: 'paid_conversion',
        referrer_reward_type: 'cash_flat',
        referrer_reward_value: 500,
        referrer_reward_recurring: 0,
        referred_reward_type: null,
        referred_reward_value: null,
      },
    ])
  }

  function updateRule(key: number, patch: Partial<CreateRulePayload>) {
    setRules((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)))
  }

  function removeRule(key: number) {
    setRules((prev) => prev.filter((r) => r._key !== key))
  }

  async function handleCreate() {
    if (!name.trim()) return
    setCreating(true)
    setError(null)
    try {
      // 1. Create campaign
      const result = await authApi.post<{ id: string }>('/api/v1/admin/referral/campaigns', {
        name: name.trim(),
        description: description.trim() || undefined,
        status,
        referrer_type: referrerType,
        referred_product: referredProduct,
        attribution_window_days: attributionWindowDays ? Number(attributionWindowDays) : 0,
        max_referrals_per_user: maxPerUser ? Number(maxPerUser) : null,
        max_total_referrals: maxTotal ? Number(maxTotal) : null,
        pending_days: pendingDays ? Number(pendingDays) : null,
        min_payout_cents: minPayoutCents ? Number(minPayoutCents) : null,
        starts_at: startsAt ? Math.floor(new Date(startsAt).getTime() / 1000) : null,
        ends_at: endsAt ? Math.floor(new Date(endsAt).getTime() / 1000) : null,
      })

      // 2. Create rules
      for (const rule of rules) {
        await authApi.post(`/api/v1/admin/referral/campaigns/${result.id}/rules`, {
          trigger_event: rule.trigger_event,
          referrer_reward_type: rule.referrer_reward_type,
          referrer_reward_value: rule.referrer_reward_value,
          referrer_reward_recurring: rule.referrer_reward_recurring,
          referred_reward_type: rule.referred_reward_type || null,
          referred_reward_value: rule.referred_reward_value || null,
        })
      }

      queryClient.invalidateQueries({ queryKey: ['admin-referral-campaigns'] })
      navigate(`/referrals/campaigns/${result.id}`)
    } catch (e) {
      setError(String(e))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div>
      <Link to="/referrals/campaigns" className="text-sm text-[var(--brand)] hover:underline mb-4 inline-block">
        &larr; Back to Campaigns
      </Link>
      <h1 className="text-2xl font-heading font-semibold mb-6">New Campaign</h1>

      <div className="space-y-6 max-w-3xl">
        {/* Campaign settings */}
        <Card>
          <CardHeader>
            <h3 className="font-heading font-semibold text-sm">Campaign Settings</h3>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Campaign Name" hint="Internal name to identify this campaign.">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. VoSci Academy Referral Program"
                className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
              />
            </Field>

            <Field label="Description" hint="Optional notes about this campaign's purpose.">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="e.g. $5 per conversion + $5/month retention commission"
                className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30 resize-y"
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Status" hint="Draft campaigns don't process rewards.">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                </select>
              </Field>

              <Field label="Product" hint="Which VoSci product this campaign refers users to.">
                <select
                  value={referredProduct}
                  onChange={(e) => setReferredProduct(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
                >
                  <option value="academy">Academy</option>
                  <option value="studio">Studio</option>
                  <option value="teacher">Teacher</option>
                  <option value="any">Any</option>
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Who Can Refer" hint="Restrict who is eligible to be a referrer.">
                <select
                  value={referrerType}
                  onChange={(e) => setReferrerType(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
                >
                  <option value="any">Anyone</option>
                  <option value="student">Students only</option>
                  <option value="teacher">Teachers only</option>
                  <option value="studio_owner">Studio Owners only</option>
                </select>
              </Field>

              <Field label="Approval Hold (days)" hint="Days to hold rewards before auto-approving. Use 14 to cover refund window.">
                <input
                  type="number"
                  value={pendingDays}
                  onChange={(e) => setPendingDays(e.target.value)}
                  placeholder="0 = instant"
                  className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
                />
              </Field>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <Field label="Attribution Window" hint="Days a referral link stays valid. Leave empty or 0 for infinite.">
                <input
                  type="number"
                  value={attributionWindowDays}
                  onChange={(e) => setAttributionWindowDays(e.target.value)}
                  placeholder="0 = Infinite"
                  className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
                />
              </Field>

              <Field label="Max Per Referrer" hint="Cap referrals per person.">
                <input
                  type="number"
                  value={maxPerUser}
                  onChange={(e) => setMaxPerUser(e.target.value)}
                  placeholder="Unlimited"
                  className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
                />
              </Field>

              <Field label="Max Total Referrals" hint="Cap for entire campaign.">
                <input
                  type="number"
                  value={maxTotal}
                  onChange={(e) => setMaxTotal(e.target.value)}
                  placeholder="Unlimited"
                  className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
                />
              </Field>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <Field label="Min Payout" hint="Minimum balance before cash-out.">
                <input
                  type="number"
                  value={minPayoutCents}
                  onChange={(e) => setMinPayoutCents(e.target.value)}
                  placeholder="1000 = $10"
                  className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
                />
              </Field>

              <Field label="Starts At">
                <input
                  type="date"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
                />
              </Field>

              <Field label="Ends At">
                <input
                  type="date"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        {/* Commission rules */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold text-sm">Commission Rules</h3>
                <p className="text-xs text-[var(--content-tertiary)] mt-0.5">
                  Define when and how much referrers earn. For ongoing monthly commissions, check "Recurring".
                </p>
              </div>
              <button
                onClick={addRule}
                className="px-3 py-1.5 text-xs rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] transition-colors cursor-pointer"
              >
                Add Rule
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {rules.length === 0 ? (
              <div className="text-center py-6 space-y-3">
                <p className="text-sm text-[var(--content-tertiary)]">
                  No commission rules yet. Add at least one rule so referrers get paid.
                </p>
                <div className="text-xs text-[var(--content-tertiary)] space-y-1">
                  <p><strong>Example — $5/month ongoing:</strong> Trigger = Paid Conversion, Type = Flat Cash, Value = 500, Recurring = Yes</p>
                  <p><strong>Example — 15% of MRR:</strong> Trigger = Paid Conversion, Type = % of MRR, Value = 1500 (basis points), Recurring = Yes</p>
                </div>
              </div>
            ) : (
              rules.map((rule, idx) => (
                <Card key={rule._key} className="bg-[var(--surface-nested)]">
                  <CardContent className="py-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-[var(--content-tertiary)] uppercase tracking-wider">
                        Rule {idx + 1}
                      </span>
                      <button
                        onClick={() => removeRule(rule._key)}
                        className="text-xs text-red-500 hover:text-red-600 cursor-pointer"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Field label="When" hint="What event triggers this commission.">
                        <select
                          value={rule.trigger_event}
                          onChange={(e) => updateRule(rule._key, { trigger_event: e.target.value })}
                          className="w-full px-3 py-2 text-sm bg-[var(--surface)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
                        >
                          {TRIGGER_EVENTS.map((ev) => (
                            <option key={ev} value={ev}>{TRIGGER_EVENT_LABELS[ev] ?? ev}</option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Referrer Reward Type" hint="How the referrer gets paid.">
                        <select
                          value={rule.referrer_reward_type}
                          onChange={(e) => updateRule(rule._key, { referrer_reward_type: e.target.value })}
                          className="w-full px-3 py-2 text-sm bg-[var(--surface)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
                        >
                          {REFERRER_REWARD_TYPES.map((t) => (
                            <option key={t} value={t}>{REWARD_TYPE_LABELS[t] ?? t}</option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Amount" hint={getAmountHint(rule.referrer_reward_type)}>
                        <input
                          type="number"
                          value={rule.referrer_reward_value}
                          onChange={(e) => updateRule(rule._key, { referrer_reward_value: Number(e.target.value) })}
                          className="w-full px-3 py-2 text-sm bg-[var(--surface)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
                        />
                      </Field>

                      <div className="flex items-end pb-2">
                        <label className="flex items-center gap-2 text-sm text-[var(--content-secondary)] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rule.referrer_reward_recurring === 1}
                            onChange={(e) => updateRule(rule._key, { referrer_reward_recurring: e.target.checked ? 1 : 0 })}
                            className="rounded"
                          />
                          <span>
                            Recurring monthly
                            {rule.referrer_reward_recurring === 1 && (
                              <Badge variant="brand" className="ml-1.5">ongoing</Badge>
                            )}
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Referred user reward (optional) */}
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Referred User Gets (optional)" hint="Reward for the person who was referred.">
                        <select
                          value={rule.referred_reward_type ?? ''}
                          onChange={(e) => updateRule(rule._key, { referred_reward_type: e.target.value || null })}
                          className="w-full px-3 py-2 text-sm bg-[var(--surface)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
                        >
                          <option value="">Nothing</option>
                          {REFERRED_REWARD_TYPES.map((t) => (
                            <option key={t} value={t}>{REWARD_TYPE_LABELS[t] ?? t}</option>
                          ))}
                        </select>
                      </Field>

                      {rule.referred_reward_type && (
                        <Field label="Referred Amount" hint={getAmountHint(rule.referred_reward_type)}>
                          <input
                            type="number"
                            value={rule.referred_reward_value ?? ''}
                            onChange={(e) => updateRule(rule._key, { referred_reward_value: e.target.value ? Number(e.target.value) : null })}
                            className="w-full px-3 py-2 text-sm bg-[var(--surface)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
                          />
                        </Field>
                      )}
                    </div>

                    {/* Preview */}
                    <div className="pt-2 border-t border-[var(--border-subtle)]">
                      <p className="text-xs text-[var(--content-tertiary)]">
                        {describeRule(rule)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        )}

        <button
          onClick={handleCreate}
          disabled={creating || !name.trim()}
          className="px-6 py-2.5 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] disabled:opacity-50 transition-colors cursor-pointer"
        >
          {creating ? 'Creating...' : `Create Campaign${rules.length > 0 ? ` with ${rules.length} Rule${rules.length > 1 ? 's' : ''}` : ''}`}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TRIGGER_EVENT_LABELS: Record<string, string> = {
  trial_signup: 'Trial Sign Up',
  paid_conversion: 'Paid Conversion',
  retention_1m: '1 Month Retention',
  retention_2m: '2 Month Retention',
  retention_3m: '3 Month Retention',
  retention_6m: '6 Month Retention',
  retention_12m: '12 Month Retention',
  studio_creation: 'Studio Created',
}

const REWARD_TYPE_LABELS: Record<string, string> = {
  cash_flat: 'Flat Cash ($)',
  cash_percent: '% of MRR',
  credit: 'Account Credit',
  free_months: 'Free Months',
  lesson_credit: 'Lesson Credits',
  class_credit: 'Class Credits',
  trial_extension: 'Trial Extension (days)',
}

function getAmountHint(type: string): string {
  switch (type) {
    case 'cash_flat':
    case 'credit': return 'Amount in cents (500 = $5.00).'
    case 'cash_percent': return 'Basis points (1500 = 15% of MRR).'
    case 'free_months': return 'Number of months.'
    case 'lesson_credit': return 'Number of free lessons.'
    case 'class_credit': return 'Number of free classes.'
    case 'trial_extension': return 'Number of extra trial days.'
    default: return 'Amount in cents.'
  }
}

function describeRule(rule: RuleDraft): string {
  const trigger = TRIGGER_EVENT_LABELS[rule.trigger_event] ?? rule.trigger_event
  const type = rule.referrer_reward_type
  let amount = ''
  if (type === 'cash_flat' || type === 'credit') {
    amount = `$${(rule.referrer_reward_value / 100).toFixed(2)}`
  } else if (type === 'cash_percent') {
    amount = `${(rule.referrer_reward_value / 100).toFixed(1)}%`
  } else {
    amount = `${rule.referrer_reward_value}`
  }

  let desc = `On ${trigger}: referrer earns ${amount} (${REWARD_TYPE_LABELS[type] ?? type})`
  if (rule.referrer_reward_recurring === 1) {
    desc += ' — paid every month the referred user stays subscribed'
  }
  if (rule.referred_reward_type) {
    const rType = REWARD_TYPE_LABELS[rule.referred_reward_type] ?? rule.referred_reward_type
    desc += `. Referred user gets ${rule.referred_reward_value} (${rType})`
  }
  return desc
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-[var(--content-tertiary)] mb-1">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-[var(--content-tertiary)] mt-0.5">{hint}</p>}
    </div>
  )
}
